/**
 * Block 12 — типы открывания фасадов (handle types).
 *
 * Применимо к 3 анкетам с автоматической формулой `facadeCount`:
 *   - **Кухня** — 3 опции (`с ручками` / `от нажатия` / `гола`).
 *   - **Шкаф / Гардероб** — 2 опции (без `гола`).
 *   - **Балкон** — 2 опции (без `гола`, OQ-13 уточнение).
 *
 * `'гола'` — алюминиевый профиль типа Гола, используется ТОЛЬКО в Кухне
 * (декоративный фасадный профиль для безручечных кухонь). На Шкафу/Балконе
 * этот вариант недопустим — формулы `facadeCountCabinet` / `facadeCountBalcony`
 * рассчитывают одни и те же штук фасадов независимо от типа открывания, и
 * Гола применяется через отдельную formula `golaCountKitchen` (только Кухня).
 *
 * **Dispatch logic** (Phase B3 template engine, Part C):
 *   - `handleType === 'гола'` (Кухня) → `golaCountKitchen(params)` (Б2 formula).
 *   - `handleType ∈ {'с ручками', 'от нажатия'}` → `facadeCount(params)` dispatcher.
 *
 * Reference:
 *   - `docs/methodology/calculator-formulas.md` §1.4, §2.1 В.1, §2.2 В.1, §2.7 В.1
 *   - ADR-0019 §Q5 (AnketaForm sections А-И)
 *   - Phase B3 Step 1 ТЗ §A.2 (Path 1 — template engine dispatches, formulas frozen)
 */

export const HANDLE_TYPES = ['с ручками', 'от нажатия', 'гола'] as const;

/** Кухня — все 3 опции (включая `гола`). Алиас на `HANDLE_TYPES`. */
export const KITCHEN_HANDLE_TYPES = HANDLE_TYPES;

/** Шкаф / Гардероб — только 2 опции (без `гола`). */
export const CABINET_HANDLE_TYPES = ['с ручками', 'от нажатия'] as const;

/** Балкон — те же 2 опции что и Шкаф (OQ-13). Alias на `CABINET_HANDLE_TYPES`. */
export const BALCONY_HANDLE_TYPES = CABINET_HANDLE_TYPES;

export type HandleType = (typeof HANDLE_TYPES)[number];
export type KitchenHandleType = (typeof KITCHEN_HANDLE_TYPES)[number];
export type CabinetHandleType = (typeof CABINET_HANDLE_TYPES)[number];
export type BalconyHandleType = (typeof BALCONY_HANDLE_TYPES)[number];

/** Русские UI лейблы для RadioCard опций (Phase B3 Part C SectionMaterials). */
export const HANDLE_TYPE_LABELS_RU: Record<HandleType, string> = {
  'с ручками': 'С ручками',
  'от нажатия': 'От нажатия (push-to-open)',
  гола: 'Гола (профиль)',
};

/** Type guard: проверяет что значение — валидный `KitchenHandleType` (3 опции). */
export function isKitchenHandleType(v: unknown): v is KitchenHandleType {
  return typeof v === 'string' && (KITCHEN_HANDLE_TYPES as readonly string[]).includes(v);
}

/** Type guard: проверяет что значение — валидный `CabinetHandleType` (2 опции, без `гола`). */
export function isCabinetHandleType(v: unknown): v is CabinetHandleType {
  return typeof v === 'string' && (CABINET_HANDLE_TYPES as readonly string[]).includes(v);
}
